/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { useIntl } from 'react-intl';

import styles from './MainNav.style';

import { getLocalizedData } from '@e180/domain/lib/base/l10n/util';

import MainNavLink from './MainNavLink';
import messages from 'src/shared/app/nav/l10n/navigationL10n';
import LazyAccountMain from 'src/shared/app/account/base/component/LazyAccountMain';
import useMobileKeyboardVisibility from 'src/shared/app/base/hook/useMobileKeyboardVisibility';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import {
  homeRoute,
  newTopicRoute,
  braindatesRoute,
  profileRoute,
} from 'src/shared/app/base/route/setting/routeSettings';
import LazyBraindatesMain from 'src/shared/app/braindate/component/main/LazyBraindatesMain';
import LazyMarketMain from 'src/shared/app/market/component/LazyMarketMain';
import LazyNewTopicMain from 'src/shared/app/topic/new/component/LazyNewTopicMain';
import UserAvatar, {
  xsmall,
} from 'src/shared/components/domain/user/avatar/UserAvatar';
import { trackGAEvent } from 'src/shared/core/service/tracking/gaService';
import useEventBraindateName from 'src/shared/domain/event/hook/useEventBraindateName';
import BellIcon from 'src/shared/ui/component/icon/BellIcon';
import HomeIcon from 'src/shared/ui/component/icon/HomeIcon';
import PlusIcon from 'src/shared/ui/component/icon/PlusIcon';
import NotificationBadge from 'src/shared/ui/component/messaging/NotificationBadge';

import type { User } from '@e180/domain/lib/user/type';

type Props = {
  user: User,
  hasBraindateNotifications: boolean,
  externalLinks: Array<Object>,
};

const useStyles = createUseStyles(styles);

const NavDumb = ({
  user,
  hasBraindateNotifications,
  externalLinks,
}: Props): React.Node => {
  /*
   |------------------------------------------------------------------------------
   | Hooks
   |------------------------------------------------------------------------------
   */

  const intl = useIntl();
  const isKeyboardShown = useMobileKeyboardVisibility();
  const { braindatePlural } = useEventBraindateName();
  const [homePath, newTopicPath, braindatesPath, profilePath] = useRoutes(
    homeRoute,
    newTopicRoute,
    braindatesRoute,
    profileRoute,
  );

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onCustomMenuClick = () =>
    trackGAEvent('Navigation', 'Click custom menu or back to app link');

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  const rootClasses = classNames(classes.root, {
    [classes['root-isHiddenOnMobile']]: isKeyboardShown,
  });

  const externalClasses = classNames(classes.item, classes.external);

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const { locale } = intl;

  const externalItems = externalLinks.map((link, index) => (
    <li className={externalClasses} onClick={onCustomMenuClick} key={index}>
      <MainNavLink
        title={(getLocalizedData(link.body, locale) || {}).label}
        target={link.target}
        to={link.href}
        icon={<img className={classes.externalIcon} src={link.icon} alt="" />}
      />
    </li>
  ));

  const badgeElt = hasBraindateNotifications && (
    <NotificationBadge layoutClass={classes.badge} />
  );

  return (
    <nav
      className={rootClasses}
      aria-label={intl.formatMessage(messages.label)}
    >
      <ul className={classes.list}>
        <li className={classes.item}>
          <MainNavLink
            title={intl.formatMessage(messages.market)}
            to={homePath}
            icon={<HomeIcon />}
            preloadComp={LazyMarketMain}
            exact
          />
        </li>
        <li className={classes.item}>
          <MainNavLink
            title={intl.formatMessage(messages.newTopic)}
            to={newTopicPath}
            icon={<PlusIcon />}
            preloadComp={LazyNewTopicMain}
          />
        </li>
        <li className={classes.item}>
          <MainNavLink
            title={braindatePlural}
            to={braindatesPath}
            icon={<BellIcon />}
            preloadComp={LazyBraindatesMain}
          >
            {badgeElt}
          </MainNavLink>
        </li>
        <li className={classes.item}>
          <MainNavLink
            title={intl.formatMessage(messages.profile)}
            to={profilePath}
            icon={
              user ? (
                <div className={classes.avatar}>
                  <UserAvatar user={user} variant={xsmall} alt="" />
                </div>
              ) : null
            }
            preloadComp={LazyAccountMain}
          ></MainNavLink>
        </li>
        {externalItems}
      </ul>
    </nav>
  );
};

export default compose(React.memo)(NavDumb);
